import { createContext, useCallback, useContext, useRef, type ReactNode } from 'react'
import { api, ApiError, type AuthSession } from './api'
import { saveSession } from './session'

type AuthorizedRequest = <T>(work: (token: string) => Promise<T>) => Promise<T>
type AuthContextValue = {
  session: AuthSession
  authorized: AuthorizedRequest
  hasPermission: (permission: string) => boolean
  hasAnyPermission: (...permissions: string[]) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ session, onSession, onExpired, children }: {
  session: AuthSession
  onSession: (session: AuthSession) => void
  onExpired: () => void
  children: ReactNode
}) {
  const sessionRef = useRef(session)
  const refreshRef = useRef<Promise<AuthSession> | null>(null)
  sessionRef.current = session

  const authorized = useCallback<AuthorizedRequest>(async work => {
    const requestedWith = sessionRef.current.accessToken
    try { return await work(requestedWith) }
    catch (reason) {
      if (!(reason instanceof ApiError) || reason.status !== 401) throw reason
      if (sessionRef.current.accessToken !== requestedWith) {
        return work(sessionRef.current.accessToken)
      }
      try {
        if (!refreshRef.current) {
          const refreshToken = sessionRef.current.refreshToken
          refreshRef.current = api.refresh(refreshToken).then(renewed => {
            sessionRef.current = renewed
            saveSession(renewed)
            onSession(renewed)
            return renewed
          }).catch(refreshError => {
            if (refreshError instanceof ApiError && refreshError.status === 401) {
              saveSession(null)
              onExpired()
            }
            throw refreshError
          }).finally(() => { refreshRef.current = null })
        }
        const renewed = await refreshRef.current
        return await work(renewed.accessToken)
      } catch (refreshError) {
        throw refreshError
      }
    }
  }, [onExpired, onSession])

  const hasPermission = useCallback((permission: string) => session.user.root || session.user.permissions.includes(permission), [session.user.permissions, session.user.root])
  const hasAnyPermission = useCallback((...permissions: string[]) => session.user.root || permissions.some(permission => session.user.permissions.includes(permission)), [session.user.permissions, session.user.root])

  return <AuthContext.Provider value={{ session, authorized, hasPermission, hasAnyPermission }}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
